import { useMemo, useState } from 'react'
import { Chip, Icon, ModelPicker, Money, PriorityPicker } from '../components/ui.jsx'
import { api, fmt } from '../lib/api.js'

// One table for the whole book.
//
// Working Capital and Procurement are not separate screens. They were never
// different DATA -- they are the same 140 accounts with a different column set
// and a different filter -- so they are views over this table. That removes
// three places where the same fit logic had to agree with itself, and it means
// a question that crosses two of them ("one criterion from Strong, has a
// contact, never touched") can actually be asked.
//
// The filters fall into three families and each chip carries its own count,
// recomputed against everything else already selected. The count is the answer;
// the rows are the evidence for it.

const OWNERSHIP_ORDER = ['Public', 'PE-owned', 'Sponsor-owned', 'Subsidiary', 'Family-owned', 'Private', 'Creditor-owned']
const BANDS = ['Strong', 'Possible', 'Weak', 'Below gate', 'Unrated']

const BAND_KIND = {
  Strong: 'named',
  Possible: 'mentioned',
  Weak: 'not_now',
  'Below gate': 'absent',
  Unrated: 'unresearched',
}

// Column groups, in the order they appear. `wc` and `proc` carry the criteria
// columns: they are wide, so they are off by default and switched on when you
// are actually working a model.
const GROUPS = [
  ['financials', 'Financials'],
  ['wc', 'Working capital'],
  ['proc', 'Procurement'],
  ['evidence', 'Evidence'],
  ['engagement', 'Engagement'],
]

// A view is nothing more than a preset: which columns, which filters, which
// sort. Keeping them as data means the nav entries survive as one click
// without surviving as one component each.
const VIEWS = {
  all: { label: 'All accounts', cols: { financials: true, engagement: true } },
  wc: {
    label: 'Working capital',
    cols: { wc: true, financials: true },
    filters: { wcBand: ['Strong', 'Possible'] },
    sort: { key: 'excess', dir: 'desc' },
  },
  proc: {
    label: 'Procurement',
    cols: { proc: true, financials: true },
    filters: { procBand: ['Strong', 'Possible'] },
    sort: { key: 'spend', dir: 'desc' },
  },
  gaps: {
    label: 'One criterion away',
    cols: { wc: true, proc: true },
    filters: { nearMiss: true },
    sort: { key: 'excess', dir: 'desc' },
  },
  untouched: {
    label: 'Strong, never touched',
    cols: { wc: true, engagement: true },
    filters: { wcBand: ['Strong'], untouched: true },
    sort: { key: 'excess', dir: 'desc' },
  },
}

const EMPTY = {
  ownership: [], wcBand: [], procBand: [], reach: [], nature: [], segment: [],
  wcCrit: {}, procCrit: {},
  nearMiss: false, provisional: false, overridden: false, interim: false,
  noFinancials: false, unresearched: false,
  untouched: false, hasContacts: false, inPlay: false,
}

// A view has to normalise to a full state before it can be compared with one:
// `cols` needs every group present, and `all` has no sort of its own.
const viewCols = (v) => ({ financials: false, wc: false, proc: false, evidence: false, engagement: false, ...v.cols })
const viewSort = (v) => v.sort || { key: 'name', dir: 'asc' }
const viewFilters = (v) => ({ ...EMPTY, ...(v.filters || {}) })

const sameSet = (a = [], b = []) => a.length === b.length && a.every((x) => b.includes(x))
const sameMap = (a = {}, b = {}) =>
  Object.keys(a).length === Object.keys(b).length && Object.keys(b).every((k) => a[k] === b[k])
const sameCols = (a, b) => Object.keys(b).every((k) => Boolean(a[k]) === Boolean(b[k]))
const sameFilters = (a, b) =>
  Object.keys(EMPTY).every((k) => {
    const x = a[k]
    const y = b[k]
    if (Array.isArray(y)) return sameSet(x, y)
    if (y && typeof y === 'object') return sameMap(x, y)
    return Boolean(x) === Boolean(y)
  })

const money = (m) => (m == null ? null : Math.round(m).toLocaleString())

// True when the interim reading is far enough from the annual one that a
// single-quarter figure would mislead. Used for the marker and the filter.
const swings = (a) =>
  Boolean(a.inv_days && a.inv_days_interim &&
    Math.abs(a.inv_days_interim - a.inv_days) / a.inv_days > 0.15)

function BandChip({ fit }) {
  if (!fit) return <span style={{ color: 'var(--tertiary)' }}>—</span>
  const band = fit.fit || 'Unrated'
  return (
    <span title={fit.overridden ? `Set by hand; model said ${fit.model_fit || 'Unrated'}` : `${fit.met} criteria met`}>
      <Chip kind={BAND_KIND[band] || 'unresearched'}>{band}</Chip>
      {fit.overridden ? <span style={{ color: 'var(--accent-ink)', marginLeft: 3 }}>*</span> : null}
    </span>
  )
}

// Met, not met, and not researched are three states, not two. A blank cell
// would read as "not met", which is the one thing it must never say.
function CritCell({ met }) {
  if (met === true) return <span style={{ color: 'var(--pos, var(--accent-ink))', fontWeight: 600 }}>Yes</span>
  if (met === false) return <span style={{ color: 'var(--tertiary)' }}>No</span>
  return <span style={{ color: 'var(--tertiary)' }} title="Not researched">?</span>
}

// Every chip shows how many rows it would leave, counted against the other
// filters already applied but not against itself -- so a multi-select shows
// what each option adds rather than collapsing to the current selection.
//
// Clicking narrows to the one you clicked. A view preset can arrive with two
// bands already lit, and clicking "Strong" on Strong+Possible used to REMOVE
// Strong and leave you with Possible -- the opposite of what the click meant.
// Shift-click still toggles, so a deliberate multi-select is a click then
// shift-clicks.
function Facet({ label, options, selected, onToggle, onOnly }) {
  if (!options.length) return null
  return (
    <>
      <span className="lbl" style={{ alignSelf: 'center', flexShrink: 0 }}>{label}</span>
      {options.map(({ value, text, count }) => (
        <button key={value} className={`btn ${selected.includes(value) ? 'on' : ''}`}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          disabled={!count && !selected.includes(value)}
          title="Click to show only this · shift-click to add or remove"
          onClick={(e) => (e.shiftKey ? onToggle(value) : onOnly(value))}>
          {text}
          <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{count}</span>
        </button>
      ))}
    </>
  )
}

function Flag({ on, count, onClick, children, title }) {
  return (
    <button className={`btn ${on ? 'on' : ''}`} onClick={onClick} title={title}
      style={{ flexShrink: 0, whiteSpace: 'nowrap' }} disabled={!count && !on}>
      {children}
      <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{count}</span>
    </button>
  )
}

// --- predicates ------------------------------------------------------------
// One function per filter, so a facet count can be taken by running every
// predicate EXCEPT its own. That is what makes the counts mean "how many would
// be left if I clicked this" rather than "how many are showing now".

const bandOf = (fit) => (fit ? fit.fit || 'Unrated' : 'Unrated')
const critOf = (fit, key) => (fit?.criteria || []).find((c) => c.key === key)?.met

const PREDICATES = {
  ownership: (a, f) => !f.ownership.length || f.ownership.includes(a.ownership),
  wcBand: (a, f) => !f.wcBand.length || f.wcBand.includes(bandOf(a.wc_fit)),
  procBand: (a, f) => !f.procBand.length || f.procBand.includes(bandOf(a.proc_fit)),
  reach: (a, f) => !f.reach.length || f.reach.includes(a.wc_fit?.reach),
  nature: (a, f) => !f.nature.length || f.nature.includes(a.wc_fit?.nature),
  segment: (a, f) => !f.segment.length || f.segment.some((k) => (a.segments || []).includes(k)),
  wcCrit: (a, f) => Object.entries(f.wcCrit).every(([k, want]) => critOf(a.wc_fit, k) === want),
  procCrit: (a, f) => Object.entries(f.procCrit).every(([k, want]) => critOf(a.proc_fit, k) === want),
  // Unresearched criteria that could still carry the account. This is the
  // research queue, ranked: fill one gap and the band may move.
  nearMiss: (a, f) => !f.nearMiss || [a.wc_fit, a.proc_fit].some(
    (r) => r && r.unresearched > 0 && r.met + r.unresearched >= 4 && r.fit !== 'Strong'),
  provisional: (a, f) => !f.provisional || Boolean(a.wc_fit?.provisional || a.proc_fit?.provisional),
  overridden: (a, f) => !f.overridden || Boolean(a.wc_fit?.overridden || a.proc_fit?.overridden),
  // The seasonality guard. `inv_days_basis` only records what the HEADLINE
  // figure was taken from, and that is annual for almost everything, so it
  // catches nothing. The question worth asking is where the interim figure
  // diverges from the annual one: Corteva swings from 4,316m to 5,667m across
  // a year, and a single-quarter reading of an account like that is close to
  // meaningless. 15% is a materiality bar, not a rule from the data.
  interim: (a, f) => !f.interim || swings(a),
  noFinancials: (a, f) => !f.noFinancials || a.inv_days == null,
  unresearched: (a, f) => !f.unresearched || !a.wc_fit?.fit || !a.proc_fit?.fit,
  untouched: (a, f) => !f.untouched || a.engagement.status === 'not_started',
  hasContacts: (a, f) => !f.hasContacts || a.contact_count > 0,
  inPlay: (a, f) => !f.inPlay || a.engagement.status !== 'not_started',
  query: (a, f) => {
    const q = (f.query || '').trim().toLowerCase()
    if (!q) return true
    if (a.name.toLowerCase().includes(q)) return true
    return (a.contacts || []).some((c) => (c.name || '').toLowerCase().includes(q))
  },
}

const passes = (a, f, except) =>
  Object.entries(PREDICATES).every(([k, fn]) => k === except || fn(a, f))

const SORTS = {
  name: (a) => a.name.toLowerCase(),
  priority: (a) => a.priority,
  inv_days: (a) => a.inv_days,
  excess: (a) => a.wc_fit?.excess_usd,
  days_over: (a) => a.wc_fit?.days_over,
  spend: (a) => a.proc_fit?.spend_usd,
  contacts: (a) => a.contact_count,
  revenue: (a) => a.revenue?.usd,
  margin: (a) => (a.margin ? parseFloat(a.margin) : null),
  updated: (a) => a.last_updated,
}

// The plant-scheduling top-up, in one line. It only exists while accounts are
// still unscored on that area, and it disappears once every one is scored --
// so it earns a row above the table, not a panel of tiles.
function TopupNotice({ stats, models, model, onModel, onChanged }) {
  const [topup, setTopup] = useState(null)
  const [compare, setCompare] = useState(null)
  const [busy, setBusy] = useState(false)

  const area = stats?.by_area?.plant_scheduling || {}
  const remaining = area.unresearched || 0
  const scored = (area.named || 0) + (area.mentioned || 0) + (area.absent || 0)
  if (!remaining) return null

  // What a top-up would cost, asked for before it is committed to.
  const preview = async (forModel) => {
    setBusy(true)
    try {
      const [est, cmp] = await Promise.all([
        api.costEstimate({ kind: 'topup', count: remaining, model: forModel || model }),
        api.costCompare({ kind: 'topup', count: remaining }),
      ])
      setTopup({ estimate: est })
      setCompare(cmp)
    } finally {
      setBusy(false)
    }
  }
  const pickModel = async (id) => {
    await onModel(id)
    if (topup && !topup.done) preview(id)
  }
  const run = async () => {
    setBusy(true)
    try {
      const out = await api.runTopup({ model })
      setTopup({ ...out, done: true })
      onChanged?.()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="notice" style={{ display: 'block', flexShrink: 0 }}>
      <div className="row">
        <Icon name="spark" width="14" height="14" style={{ color: 'var(--ai-ink)', flexShrink: 0 }} />
        <span className="notice-body" style={{ minWidth: 0 }}>
          <strong>Plant Scheduling</strong>{' '}
          {scored ? `scored for ${scored} accounts, ${remaining} to go.` : 'has never been researched.'}{' '}
          A top-up re-reads the remaining {remaining} against that one area and writes a brief for a research agent.
        </span>
        <button className="btn" style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }} disabled={busy}
          onClick={topup && !topup.done ? run : () => preview()}>
          {topup?.done ? 'Brief written' : topup ? 'Confirm and write brief' : 'Estimate top-up'}
        </button>
      </div>
      {topup ? (
        <div className="notice-body" style={{ marginTop: 6, borderTop: '0.5px solid var(--ai-border)', paddingTop: 6 }}>
          {topup.done ? (
            <>
              Brief written for {topup.companies} accounts, estimated{' '}
              <span className="mono">{topup.estimate.usd_display}</span>. Hand it to a research agent, then import
              the result on the Not researched screen.
              <div className="mono" style={{ fontSize: 'var(--fs-badge)', marginTop: 4 }}>{topup.brief_path}</div>
            </>
          ) : (
            <>
              <div style={{ marginBottom: 6 }}>
                Estimated <span className="mono" style={{ fontWeight: 600 }}>{topup.estimate.usd_display}</span> for{' '}
                {topup.estimate.count} accounts on {topup.estimate.model_label}, about{' '}
                <span className="mono">${topup.estimate.per_unit_usd.toFixed(2)}</span> each. {topup.estimate.caveat}
              </div>
              <ModelPicker value={model} options={models} onChange={pickModel} compare={compare} count={remaining} />
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}

export default function Accounts({ accounts, stats, onOpen, view = 'all', segments = [], models, model, onModel, onChanged }) {
  const preset = VIEWS[view] || VIEWS.all
  const [cols, setCols] = useState(viewCols(preset))
  const [f, setF] = useState({ ...viewFilters(preset), query: '' })
  const [sort, setSort] = useState(viewSort(preset))
  // The view you chose, kept until you choose a different one. Refining the
  // filters inside a view does not leave it.
  const [pickedView, setPickedView] = useState(view)
  // Five rows of filters pushed the table off the bottom of the screen. Bands
  // and ownership stay pinned because they are the ones reached for every
  // session; the rest open on demand and announce how many are active so a
  // filter can never be hidden AND forgotten.
  const [more, setMore] = useState(false)

  const set = (patch) => setF((v) => ({ ...v, ...patch }))
  const toggleIn = (key, value) =>
    setF((v) => ({ ...v, [key]: v[key].includes(value) ? v[key].filter((x) => x !== value) : [...v[key], value] }))
  // A plain click means "show me this one". Clicking the one already showing
  // alone means "stop filtering by this", which is the only sensible way back
  // to everything without hunting for a clear button.
  const onlyIn = (key, value) =>
    setF((v) => ({ ...v, [key]: v[key].length === 1 && v[key][0] === value ? [] : [value] }))
  // Criterion filters cycle met -> not met -> off, because "which accounts does
  // footprint disqualify" is as useful a question as "which does it carry".
  const cycleCrit = (group, key) =>
    setF((v) => {
      const cur = v[group][key]
      const next = { ...v[group] }
      if (cur === undefined) next[key] = true
      else if (cur === true) next[key] = false
      else delete next[key]
      return { ...v, [group]: next }
    })

  const setPriority = async (slug, value) => {
    await api.setPriority(slug, value)
    onChanged?.()
  }

  const rows = useMemo(() => {
    const list = accounts.filter((a) => passes(a, f))
    const pick = SORTS[sort.key] || SORTS.name
    return [...list].sort((x, y) => {
      const a = pick(x)
      const b = pick(y)
      if (a == null && b == null) return x.name.localeCompare(y.name)
      if (a == null) return 1
      if (b == null) return -1
      const c = typeof a === 'string' ? a.localeCompare(b) : a - b
      return sort.dir === 'desc' ? -c : c
    })
  }, [accounts, f, sort])

  // Facet counts: for each option, how many rows would remain if it were the
  // only value selected in its own family.
  const counts = useMemo(() => {
    const pool = (except) => accounts.filter((a) => passes(a, f, except))
    const tally = (except, keyFn) => {
      const m = new Map()
      for (const a of pool(except)) {
        for (const k of [].concat(keyFn(a))) if (k != null) m.set(k, (m.get(k) || 0) + 1)
      }
      return m
    }
    return {
      ownership: tally('ownership', (a) => a.ownership),
      wcBand: tally('wcBand', (a) => bandOf(a.wc_fit)),
      procBand: tally('procBand', (a) => bandOf(a.proc_fit)),
      reach: tally('reach', (a) => a.wc_fit?.reach),
      nature: tally('nature', (a) => a.wc_fit?.nature),
      segment: tally('segment', (a) => a.segments || []),
      flag: Object.fromEntries(
        ['nearMiss', 'provisional', 'overridden', 'interim', 'noFinancials', 'unresearched',
          'untouched', 'hasContacts', 'inPlay']
          .map((k) => [k, pool(k).filter((a) => PREDICATES[k](a, { ...f, [k]: true })).length]),
      ),
    }
  }, [accounts, f])

  // The header is the answer to the question the filters just asked.
  const totals = useMemo(() => ({
    accounts: rows.length,
    contacts: rows.reduce((n, a) => n + (a.contact_count || 0), 0),
    excess: rows.reduce((n, a) => n + (a.wc_fit?.excess_usd || 0), 0),
    spend: rows.reduce((n, a) => n + (a.proc_fit?.spend_usd || 0), 0),
  }), [rows])

  // Which preset the current state matches exactly, if any. Only used to light
  // a view you have not explicitly picked -- arriving from the nav, say. The
  // view you did pick stays lit while you refine inside it.
  const exactView = Object.entries(VIEWS).find(([, v]) =>
    sameCols(cols, viewCols(v)) &&
    sameFilters(f, viewFilters(v)) &&
    sort.key === viewSort(v).key && sort.dir === viewSort(v).dir)?.[0] || null
  const activeView = pickedView || exactView

  const opts = (key, order) => {
    const m = counts[key]
    const keys = order ? order.filter((k) => m.has(k) || f[key].includes(k)) : [...m.keys()].sort()
    return keys.map((k) => ({ value: k, text: k, count: m.get(k) || 0 }))
  }

  const wcKeys = accounts.find((a) => a.wc_fit?.criteria?.length)?.wc_fit.criteria || []
  const procKeys = accounts.find((a) => a.proc_fit?.criteria?.length)?.proc_fit.criteria || []
  const countActive = (keys) =>
    keys.filter((k) => {
      const v = f[k]
      return Array.isArray(v) ? v.length : v && typeof v === 'object' ? Object.keys(v).length : v
    }).length
  const active = countActive(Object.keys(f).filter((k) => k !== 'query'))
  const hiddenActive = countActive([
    'wcCrit', 'procCrit', 'segment', 'nearMiss', 'provisional', 'overridden',
    'interim', 'noFinancials', 'unresearched', 'untouched', 'hasContacts', 'inPlay',
  ])

  const Sortable = ({ k, children, ...rest }) => (
    <th {...rest} style={{ ...rest.style, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => setSort((s) => ({ key: k, dir: s.key === k && s.dir === 'desc' ? 'asc' : 'desc' }))}
      title="Sort">
      {children}
      {sort.key === k ? <span style={{ color: 'var(--accent-ink)' }}>{sort.dir === 'desc' ? ' ↓' : ' ↑'}</span> : null}
    </th>
  )

  const nd = <span style={{ color: 'var(--tertiary)' }}>—</span>

  return (
    <>
      <div className="topbar">
        <h1>Accounts</h1>
        <span className="topbar-note">
          {totals.accounts} of {accounts.length} · {totals.contacts} contacts
          {totals.excess ? ` · ${money(totals.excess)}m excess inventory` : ''}
          {cols.proc && totals.spend ? ` · ${money(totals.spend)}m addressable spend` : ''}
        </span>
        <div className="topbar-right">
          <div className="row" style={{ border: '0.5px solid var(--line)', background: 'var(--bg)', padding: '3px 8px', width: 190 }}>
            <Icon name="search" width="12" height="12" strokeWidth="2" style={{ color: 'var(--tertiary)' }} />
            <input className="input" style={{ border: 'none', background: 'none', padding: 0 }}
              placeholder="Search company or contact" value={f.query}
              onChange={(e) => set({ query: e.target.value })} />
          </div>
          {active ? (
            <button className="btn"
              onClick={() => { setPickedView(null); setF({ ...EMPTY, query: f.query }) }}>
              Clear {active}
            </button>
          ) : null}
        </div>
      </div>

      <div className="body accounts-dense" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 6 }}>
        <TopupNotice stats={stats} models={models} model={model} onModel={onModel} onChanged={onChanged} />

        <div className="row" style={{ gap: 6, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <span className="lbl" style={{ alignSelf: 'center', flexShrink: 0 }}>Views</span>
          {Object.entries(VIEWS).map(([key, v]) => (
            <button key={key} className={`btn ${activeView === key ? 'on' : ''}`}
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }} onClick={() => {
              setPickedView(key)
              setCols(viewCols(v))
              setF({ ...viewFilters(v), query: f.query })
              setSort(viewSort(v))
            }}>{v.label}</button>
          ))}
          <span style={{ width: 8 }} />
          <span className="lbl" style={{ alignSelf: 'center', flexShrink: 0 }}>Columns</span>
          {GROUPS.map(([key, label]) => (
            <button key={key} className={`btn ${cols[key] ? 'on' : ''}`}
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              onClick={() => setCols((c) => ({ ...c, [key]: !c[key] }))}>{label}</button>
          ))}
        </div>

        {/* The two models get a row of their own so the bands never wrap into
            each other: at nine and eight chips they did not fit beside the
            structural facets on anything narrower than a very wide screen. */}
        <div className="row" style={{ gap: 6, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Facet label="Working capital" options={opts('wcBand', BANDS)} selected={f.wcBand}
            onToggle={(v) => toggleIn('wcBand', v)} onOnly={(v) => onlyIn('wcBand', v)} />
          <span style={{ width: 10, flexShrink: 0 }} />
          <Facet label="Procurement" options={opts('procBand', BANDS)} selected={f.procBand}
            onToggle={(v) => toggleIn('procBand', v)} onOnly={(v) => onlyIn('procBand', v)} />
        </div>

        <div className="row" style={{ gap: 6, flexWrap: 'nowrap', overflowX: 'auto' }}>
          <Facet label="Reach" options={opts('reach')} selected={f.reach} onToggle={(v) => toggleIn('reach', v)} onOnly={(v) => onlyIn('reach', v)} />
          <Facet label="Nature" options={opts('nature')} selected={f.nature} onToggle={(v) => toggleIn('nature', v)} onOnly={(v) => onlyIn('nature', v)} />
          <Facet label="Ownership" options={opts('ownership', OWNERSHIP_ORDER)} selected={f.ownership}
            onToggle={(v) => toggleIn('ownership', v)} onOnly={(v) => onlyIn('ownership', v)} />
          <button className={`btn ${more || hiddenActive ? 'on' : ''}`}
            style={{ marginLeft: 'auto', flexShrink: 0 }} onClick={() => setMore((v) => !v)}>
            {more ? 'Fewer filters' : 'More filters'}
            {hiddenActive ? <span className="mono" style={{ marginLeft: 4 }}>{hiddenActive}</span> : null}
          </button>
        </div>

        {/* Criteria: click once for met, twice for not met, three times to clear */}
        {more || hiddenActive ? (
        <>
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className="lbl" style={{ alignSelf: 'center' }}>Carried by</span>
          {wcKeys.map((c) => (
            <button key={c.key} className={`btn ${f.wcCrit[c.key] !== undefined ? 'on' : ''}`}
              onClick={() => cycleCrit('wcCrit', c.key)}
              title="Click for met, again for not met, again to clear">
              {c.short}{f.wcCrit[c.key] === false ? ' ✕' : f.wcCrit[c.key] === true ? ' ✓' : ''}
            </button>
          ))}
          <span style={{ width: 8 }} />
          {procKeys.map((c) => (
            <button key={c.key} className={`btn ${f.procCrit[c.key] !== undefined ? 'on' : ''}`}
              onClick={() => cycleCrit('procCrit', c.key)}
              title="Click for met, again for not met, again to clear">
              {c.short}{f.procCrit[c.key] === false ? ' ✕' : f.procCrit[c.key] === true ? ' ✓' : ''}
            </button>
          ))}
        </div>

        {/* Evidence quality and engagement */}
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <span className="lbl" style={{ alignSelf: 'center' }}>Evidence</span>
          <Flag on={f.nearMiss} count={counts.flag.nearMiss} onClick={() => set({ nearMiss: !f.nearMiss })}
            title="Unresearched criteria that could still carry the account">One criterion away</Flag>
          <Flag on={f.provisional} count={counts.flag.provisional} onClick={() => set({ provisional: !f.provisional })}
            title="Band could still move once the gaps are filled">Provisional</Flag>
          <Flag on={f.interim} count={counts.flag.interim} onClick={() => set({ interim: !f.interim })}
            title="Interim inventory days diverge from the annual figure by more than 15% - read these with the season in mind">Seasonal swing</Flag>
          <Flag on={f.noFinancials} count={counts.flag.noFinancials} onClick={() => set({ noFinancials: !f.noFinancials })}
            title="No inventory days on file">No financials</Flag>
          <Flag on={f.unresearched} count={counts.flag.unresearched} onClick={() => set({ unresearched: !f.unresearched })}
            title="At least one model has never scored this account">Unrated by a model</Flag>
          <Flag on={f.overridden} count={counts.flag.overridden} onClick={() => set({ overridden: !f.overridden })}
            title="A person changed the band the model proposed">Overridden</Flag>
          <span style={{ width: 8 }} />
          <span className="lbl" style={{ alignSelf: 'center' }}>Engagement</span>
          <Flag on={f.hasContacts} count={counts.flag.hasContacts} onClick={() => set({ hasContacts: !f.hasContacts })}>Has contacts</Flag>
          <Flag on={f.untouched} count={counts.flag.untouched} onClick={() => set({ untouched: !f.untouched })}>Never touched</Flag>
          <Flag on={f.inPlay} count={counts.flag.inPlay} onClick={() => set({ inPlay: !f.inPlay })}>In play</Flag>
        </div>

        {segments.length ? (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            <Facet label="Segment" selected={f.segment}
              onToggle={(v) => toggleIn('segment', v)} onOnly={(v) => onlyIn('segment', v)}
              options={segments
                .filter((s) => counts.segment.has(s.key) || f.segment.includes(s.key))
                .map((s) => ({ value: s.key, text: s.label, count: counts.segment.get(s.key) || 0 }))} />
          </div>
        ) : null}
        </>
        ) : null}

        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <Sortable k="priority" style={{ width: 52 }}>Priority</Sortable>
                  <Sortable k="name">Company</Sortable>
                  <th style={{ width: 104 }}>HQ</th>
                  <th style={{ width: 92 }}>Ownership</th>
                  {cols.financials ? (
                    <>
                      <Sortable k="revenue" className="num" style={{ width: 100 }}>Revenue</Sortable>
                      <th className="num" style={{ width: 100 }}>Cost of goods</th>
                      <Sortable k="margin" className="num" style={{ width: 66 }}>Margin</Sortable>
                    </>
                  ) : null}
                  <Sortable k="inv_days" className="num" style={{ width: 66 }}>Inv days</Sortable>
                  <th className="mid" style={{ width: 92 }}>WC fit</th>
                  {cols.wc ? (
                    <>
                      {wcKeys.map((c) => <th key={c.key} className="mid" style={{ width: 62 }}>{c.short}</th>)}
                      <Sortable k="excess" className="num" style={{ width: 88 }}>Excess<span className="th-unit">USD m</span></Sortable>
                      <Sortable k="days_over" className="num" style={{ width: 66 }}>Days over</Sortable>
                    </>
                  ) : null}
                  <th className="mid" style={{ width: 92 }}>Proc fit</th>
                  {cols.proc ? (
                    <>
                      {procKeys.map((c) => <th key={c.key} className="mid" style={{ width: 62 }}>{c.short}</th>)}
                      <Sortable k="spend" className="num" style={{ width: 96 }}>Spend<span className="th-unit">USD m</span></Sortable>
                    </>
                  ) : null}
                  <Sortable k="contacts" className="num" style={{ width: 62 }}>Contacts</Sortable>
                  {cols.evidence ? (
                    <>
                      <th className="num" style={{ width: 66 }}>Interim<span className="th-unit">days</span></th>
                      <th style={{ width: 60 }}>Basis</th>
                      <Sortable k="updated" style={{ width: 82 }}>Updated</Sortable>
                    </>
                  ) : null}
                  {cols.engagement ? (
                    <>
                      <th style={{ width: 96 }}>Engagement</th>
                      <th>Next step</th>
                    </>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.slug} onClick={() => onOpen(a.slug)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <PriorityPicker value={a.priority} onChange={(v) => setPriority(a.slug, v)} />
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{a.name}</span>{' '}
                      {a.ticker ? <span className="mono" style={{ color: 'var(--tertiary)', fontSize: 'var(--fs-badge)' }}>{a.ticker}</span> : null}
                    </td>
                    <td style={{ color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 104 }}
                      title={a.hq || ''}>{a.hq || '—'}</td>
                    <td>
                      {a.ownership ? (
                        <Chip kind={a.ownership === 'PE-owned' ? 'sequenced' : a.ownership === 'Public' ? 'plain' : 'awaiting'}>
                          {a.ownership.toUpperCase()}
                        </Chip>
                      ) : <span className="chip absent">—</span>}
                    </td>
                    {cols.financials ? (
                      <>
                        <td className="num"><Money value={a.revenue} fallback={a.revenue_fallback} /></td>
                        <td className="num"><Money value={a.cogs} /></td>
                        <td className="num" title={a.margin_basis || ''}>{a.margin || nd}</td>
                      </>
                    ) : null}
                    <td className="num" style={a.inv_days > 160 ? { color: 'var(--neg)', fontWeight: 600 } : undefined}
                      title={swings(a)
                        ? `Interim reads ${Math.round(a.inv_days_interim)} days against ${Math.round(a.inv_days)} annual - seasonal`
                        : a.inv_days_basis || ''}>
                      {fmt.days(a.inv_days)}{swings(a) ? '*' : ''}
                    </td>
                    <td className="mid"><BandChip fit={a.wc_fit} /></td>
                    {cols.wc ? (
                      <>
                        {wcKeys.map((c) => (
                          <td key={c.key} className="mid"><CritCell met={critOf(a.wc_fit, c.key)} /></td>
                        ))}
                        <td className="num mono">{money(a.wc_fit?.excess_usd) || nd}</td>
                        <td className="num mono">{a.wc_fit?.days_over ? Math.round(a.wc_fit.days_over) : nd}</td>
                      </>
                    ) : null}
                    <td className="mid"><BandChip fit={a.proc_fit} /></td>
                    {cols.proc ? (
                      <>
                        {procKeys.map((c) => (
                          <td key={c.key} className="mid"><CritCell met={critOf(a.proc_fit, c.key)} /></td>
                        ))}
                        <td className="num mono" title={a.proc_fit?.below_gate ? 'Below the USD 300m gate' : ''}>
                          {money(a.proc_fit?.spend_usd) || nd}
                        </td>
                      </>
                    ) : null}
                    <td className="num">{a.contact_count || nd}</td>
                    {cols.evidence ? (
                      <>
                        <td className="num mono" title="Most recent interim period"
                          style={{ color: swings(a) ? 'var(--neg)' : 'var(--muted)' }}>
                          {a.inv_days_interim == null ? '—' : Math.round(a.inv_days_interim)}
                        </td>
                        <td className="mono" style={{ color: 'var(--muted)' }}>{a.inv_days_basis || '—'}</td>
                        <td className="mono" style={{ color: 'var(--muted)' }}>{fmt.date(a.last_updated)}</td>
                      </>
                    ) : null}
                    {cols.engagement ? (
                      <>
                        <td>
                          {a.engagement.status === 'not_started'
                            ? <span className="chip absent">—</span>
                            : <Chip kind={a.engagement.status}>{fmt.status(a.engagement.status)}</Chip>}
                        </td>
                        <td style={{ color: a.engagement.next_step ? 'var(--ink)' : 'var(--tertiary)' }}>
                          {a.engagement.next_step
                            ? `${a.engagement.next_step.text}${a.engagement.next_step.due ? `, ${fmt.date(a.engagement.next_step.due)}` : ''}`
                            : 'Not started'}
                        </td>
                      </>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row" style={{ padding: '7px 12px', borderTop: '0.5px solid var(--line)', flexShrink: 0 }}>
            <span className="card-sub">
              Money in USD millions at {stats?.fx?.as_of} rates. An asterisk on inventory days means the interim reading diverges from the annual one.
              Yes / No / ? on a criterion is met, not met, not researched.
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
